/**
 * Reads palette color tokens from CSS custom properties.
 *
 * Single source of truth for colors is `src/scss/_root-variables.scss`
 * (plus `_theme-override.scss` if it's loaded). Vuestic can't work with
 * `var(...)` in its own color calculations, so we read the computed values
 * here and hand it real colors instead.
 *
 * `main.scss` is imported on the first line of `main.ts`, so by the time
 * this runs the styles are already in the DOM. The fallback covers SSR /
 * early-call cases.
 */

const root = (): CSSStyleDeclaration | null =>
  typeof document !== 'undefined' ? getComputedStyle(document.documentElement) : null

/** Raw token value, e.g. `--c-info` -> `#158de3`. */
export function token(name: string, fallback: string): string {
  const value = root()?.getPropertyValue(name).trim()
  return value || fallback
}

/**
 * RGB triplet token wrapped in `rgb()` / `rgba()`.
 * `rgbToken('--c-primary-rgb', '44, 49, 112')` -> `rgb(44, 49, 112)`
 */
export function rgbToken(name: string, fallbackTriplet: string, alpha?: number): string {
  const triplet = root()?.getPropertyValue(name).trim() || fallbackTriplet
  return alpha === undefined ? `rgb(${triplet})` : `rgba(${triplet}, ${alpha})`
}
